import * as React from 'react';
import * as ReactDOM from 'react-dom';
const { useEffect, useLayoutEffect, useRef, useState } = React;
const h = React.createElement;

export interface DropdownMenuItem {
  key?: React.Key;
  type?: 'divider';
  label?: React.ReactNode;
  danger?: boolean;
  onClick?: (item: DropdownMenuItem) => void;
}

export type DropdownPlacement = 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';

export interface DropdownProps {
  menu?: { items?: DropdownMenuItem[] };
  trigger?: Array<'hover' | 'click'>;
  placement?: DropdownPlacement;
  /** 兼容 Ant Design API，但本实现始终 portal 到 document.body，仅保留类型 */
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  children?: React.ReactNode;
  className?: string;
}

interface Position {
  top: number;
  left: number;
}

const MIN_WIDTH = 160;
const GAP = 4;
const HOVER_LEAVE_DELAY = 120;

/** 计算下拉面板在视口的 fixed 坐标，附带边界保护 */
function computePosition(
  triggerRect: DOMRect,
  placement: DropdownPlacement,
  menuWidth: number,
  menuHeight: number,
): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = 0;
  let left = 0;

  if (placement === 'bottomLeft') {
    top = triggerRect.bottom + GAP;
    left = triggerRect.left;
  } else if (placement === 'bottomRight') {
    top = triggerRect.bottom + GAP;
    left = triggerRect.right - menuWidth;
  } else if (placement === 'topLeft') {
    top = triggerRect.top - GAP - menuHeight;
    left = triggerRect.left;
  } else if (placement === 'topRight') {
    top = triggerRect.top - GAP - menuHeight;
    left = triggerRect.right - menuWidth;
  }

  // 视口边界保护
  if (left + menuWidth > vw - 4) left = vw - menuWidth - 4;
  if (left < 4) left = 4;
  if (top + menuHeight > vh - 4) {
    top = Math.max(4, triggerRect.top - GAP - menuHeight);
  }
  if (top < 4) top = 4;

  return { top, left };
}

const Dropdown: React.FC<DropdownProps> = function Dropdown(props) {
  const { menu, trigger = ['hover'], placement = 'bottomLeft', children, className = '' } = props;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const items = menu?.items || [];
  const hoverMode = trigger.includes('hover');
  const clickMode = trigger.includes('click');

  const cancelLeave = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const scheduleLeave = () => {
    cancelLeave();
    leaveTimerRef.current = setTimeout(() => setOpen(false), HOVER_LEAVE_DELAY);
  };

  const updatePosition = () => {
    const trg = triggerRef.current;
    if (!trg) return;
    const rect = trg.getBoundingClientRect();
    const m = menuRef.current;
    const w = m?.offsetWidth || MIN_WIDTH;
    const hgt = m?.offsetHeight || 0;
    setPos(computePosition(rect, placement, w, hgt));
  };

  // 打开后测一次真实尺寸并校正
  useLayoutEffect(() => {
    if (open) updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 打开期间跟随滚动/窗口变化
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // 卸载时清理 timer
  useEffect(() => () => cancelLeave(), []);

  const triggerHandlers: Record<string, unknown> = {};
  if (clickMode) {
    triggerHandlers.onClick = () => setOpen((v) => !v);
  }
  if (hoverMode) {
    triggerHandlers.onMouseEnter = () => {
      cancelLeave();
      setOpen(true);
    };
    triggerHandlers.onMouseLeave = () => {
      scheduleLeave();
    };
  }

  const menuNode =
    open &&
    h(
      'div',
      {
        ref: menuRef,
        className:
          'fixed min-w-[160px] bg-dark-800 border border-dark-700 rounded-lg shadow-xl py-1',
        style: {
          top: pos.top,
          left: pos.left,
          zIndex: 1050,
        },
        onMouseEnter: hoverMode ? cancelLeave : undefined,
        onMouseLeave: hoverMode ? scheduleLeave : undefined,
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          const target = e.target as HTMLElement;
          if (!target.closest('.tw-dropdown-item')) setOpen(false);
        },
      },
      items.map((item, i) => {
        if (item.type === 'divider') {
          return h('div', { key: i, className: 'my-1 border-t border-dark-700' });
        }
        return h(
          'div',
          {
            key: item.key ?? i,
            className: `tw-dropdown-item px-3 py-2 text-sm cursor-pointer transition-colors ${item.danger ? 'text-red-400 hover:bg-red-500/10' : 'text-dark-200 hover:bg-dark-700'}`,
            onClick: () => {
              if (item.onClick) item.onClick(item);
              setOpen(false);
            },
          },
          item.label,
        );
      }),
    );

  return h(
    React.Fragment,
    null,
    h(
      'div',
      {
        ref: triggerRef,
        className: `inline-block ${className}`,
        ...triggerHandlers,
      },
      children,
    ),
    menuNode && typeof document !== 'undefined'
      ? ReactDOM.createPortal(menuNode, document.body)
      : null,
  );
};

export { Dropdown };
